import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Highlight from "@/pages/search/components/Highlight";
import { articles, type Article } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";

type SortType = "relevance" | "time";

function scoreArticle(article: Article, keyword: string) {
  const key = keyword.toLowerCase();
  let score = 0;
  if (article.title.toLowerCase().includes(key)) score += 5;
  if (article.tags.some((tag) => tag.toLowerCase().includes(key))) score += 3;
  if (article.summary.toLowerCase().includes(key)) score += 2;
  if (article.categoryName.toLowerCase().includes(key)) score += 2;
  const text = article.content.map((block) => `${block.text || ""}${block.caption || ""}`).join("");
  if (text.toLowerCase().includes(key)) score += 1;
  return score;
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [input, setInput] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const [sort, setSort] = useState<SortType>("relevance");
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    setInput(initialQuery);
    setQuery(initialQuery);
  }, [initialQuery]);

  useSeo({
    title: query ? `搜索「${query}」 - 黑人文化资讯 - 黑人` : "搜索 - 黑人文化资讯 - 黑人",
    description: "在全站范围内检索黑人文化、历史、音乐、影视、艺术与社会动态等领域的深度资讯。",
    keywords: "黑人文化搜索,黑人资讯检索,黑人历史,黑人音乐,黑人艺术",
  });

  const results = useMemo(() => {
    const key = query.trim();
    if (!key) return [];
    const matched = articles.filter((article) => scoreArticle(article, key) > 0);
    const filtered = matched.filter(
      (article) => activeCategory === "all" || article.category === activeCategory
    );
    return filtered.sort((a, b) =>
      sort === "time"
        ? b.date.localeCompare(a.date)
        : scoreArticle(b, key) - scoreArticle(a, key)
    );
  }, [query, sort, activeCategory]);

  const hotArticles = useMemo(() => [...articles].sort((a, b) => b.views - a.views).slice(0, 4), []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = input.trim();
    setQuery(next);
    if (next) {
      setSearchParams({ q: next });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div>
      <section className="bg-primary-600">
        <div className="mx-auto max-w-content px-4 md:px-8 py-12 md:py-16">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
            站内搜索
          </h1>
          <p className="mt-2 text-sm text-primary-100">
            输入关键词，快速找到你感兴趣的黑人文化资讯。
          </p>

          <form onSubmit={submit} className="mt-6 max-w-2xl">
            <div className="flex items-center gap-2 bg-background-50 rounded-md p-1.5">
              <span className="w-10 h-10 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-lg" />
              </span>
              <input
                type="text"
                name="q"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="搜索历史、音乐、影视、艺术、社会动态…"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400 py-2.5"
              />
              <button
                type="submit"
                className="px-6 py-2.5 rounded-md bg-accent-500 text-secondary-950 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </div>
          </form>
        </div>
      </section>

      <div className="mx-auto max-w-content px-4 md:px-8 py-10 md:py-14">
        {query ? (
          <>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
              <p className="text-sm text-foreground-600">
                关键词「<span className="text-primary-600 font-medium">{query}</span>」共找到{" "}
                <span className="text-primary-600 font-medium">{results.length}</span> 条结果
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500 shrink-0">分类：</span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setActiveCategory("all")}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                        activeCategory === "all"
                          ? "bg-primary-600 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-primary-50"
                      }`}
                    >
                      全部
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.slug}
                        type="button"
                        onClick={() => setActiveCategory(cat.slug)}
                        className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                          activeCategory === cat.slug
                            ? "bg-primary-600 text-background-50"
                            : "bg-background-100 text-foreground-600 hover:bg-primary-50"
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => setSort("relevance")}
                    className={`px-4 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      sort === "relevance" ? "bg-background-50 text-primary-700 shadow-sm" : "text-foreground-500"
                    }`}
                  >
                    相关度
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("time")}
                    className={`px-4 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      sort === "time" ? "bg-background-50 text-primary-700 shadow-sm" : "text-foreground-500"
                    }`}
                  >
                    时间
                  </button>
                </div>
              </div>
            </div>

            {results.length > 0 ? (
              <div className="space-y-4">
                {results.map((article) => (
                  <Link
                    key={article.id}
                    to={`/detail/${article.id}`}
                    className="group flex flex-col sm:flex-row gap-4 bg-background-50 rounded-lg border border-background-200 p-3 md:p-4 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-full sm:w-44 h-40 sm:h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={article.cover}
                        alt={article.title}
                        title={`${article.title} - 黑人文化资讯`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                          {article.categoryName}
                        </span>
                      </div>
                      <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
                        <Highlight text={article.title} keyword={query} />
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        <Highlight text={article.summary} keyword={query} />
                      </p>
                      <div className="mt-auto pt-3 flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
                        <span>{article.author}</span>
                        <span className="w-1 h-1 rounded-full bg-background-400" />
                        <span>{article.date}</span>
                        <span className="w-1 h-1 rounded-full bg-background-400" />
                        <span>{article.views.toLocaleString()} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="py-16 text-center bg-background-50 rounded-lg border border-background-200">
                <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-eye-line text-3xl" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-800">
                  未找到相关内容
                </h3>
                <p className="mt-2 text-sm text-foreground-500">
                  试试更换关键词，或从下方热门分类与文章开始浏览。
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-accent-500`} />
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {results.length === 0 && (
              <div className="mt-10">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">热门文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {hotArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/detail/${article.id}`}
                      className="group bg-background-50 rounded-lg border border-background-200 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <div className="h-32 overflow-hidden bg-background-200">
                        <img
                          src={article.cover}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
                          {article.title}
                        </h4>
                        <span className="mt-2 block text-xs text-foreground-400">
                          {article.categoryName}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8 h-full">
                <div className="w-12 h-12 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className="ri-search-2-line text-2xl" />
                </div>
                <h2 className="mt-5 font-heading text-xl font-bold text-foreground-900">
                  开始你的文化探索
                </h2>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  输入任意关键词，例如「爵士」「民权」「纺织」「教育」，即可在全站内容中检索相关深度资讯。
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {["爵士", "民权运动", "嘻哈", "当代艺术", "教育", "饮食文化"].map((word) => (
                    <button
                      key={word}
                      type="button"
                      onClick={() => {
                        setInput(word);
                        setQuery(word);
                        setSearchParams({ q: word });
                      }}
                      className="px-3 py-1.5 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {word}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                  热门阅读
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {hotArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/detail/${article.id}`}
                      className="group flex items-start gap-3 p-3 rounded-md hover:bg-primary-50 transition-colors cursor-pointer"
                    >
                      <div className="w-14 h-14 rounded-md overflow-hidden shrink-0 bg-background-200">
                        <img
                          src={article.cover}
                          alt={article.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
                          {article.title}
                        </span>
                        <span className="block text-xs text-foreground-400 mt-1">
                          {article.categoryName} · {article.views.toLocaleString()}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}